import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

import type {
  PlatformSafeguard,
  UpdatePlatformSafeguardRequest,
} from '../../services/organizationService';

interface DialpadSafeguardsPanelProps {
  policy: PlatformSafeguard | null;
  isLoading: boolean;
  isSaving: boolean;
  onSave: (data: UpdatePlatformSafeguardRequest) => Promise<void> | void;
}

const DialpadSafeguardsPanel: React.FC<DialpadSafeguardsPanelProps> = ({
  policy,
  isLoading,
  isSaving,
  onSave,
}) => {
  const [hardLimitEnabled, setHardLimitEnabled] = React.useState(false);
  const [usersCap, setUsersCap] = React.useState('');
  const [officesCap, setOfficesCap] = React.useState('');
  const [departmentsCap, setDepartmentsCap] = React.useState('');
  const [defaultOfficeId, setDefaultOfficeId] = React.useState('');

  React.useEffect(() => {
    setHardLimitEnabled(Boolean(policy?.hard_limit_enabled));
    setUsersCap(policy?.entity_caps.users?.toString() ?? '');
    setOfficesCap(policy?.entity_caps.offices?.toString() ?? '');
    setDepartmentsCap(policy?.entity_caps.departments?.toString() ?? '');
    setDefaultOfficeId(policy?.default_office_id ?? '');
  }, [policy]);

  const parseCap = (value: string): number | null => {
    if (value === '') {
      return null;
    }

    const parsedValue = Number(value);
    if (!Number.isFinite(parsedValue) || parsedValue < 0) {
      return null;
    }

    return parsedValue;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const entityCaps: UpdatePlatformSafeguardRequest['entity_caps'] = {};
    const parsedUsersCap = parseCap(usersCap);
    if (parsedUsersCap !== null) {
      entityCaps.users = parsedUsersCap;
    }
    const parsedOfficesCap = parseCap(officesCap);
    if (parsedOfficesCap !== null) {
      entityCaps.offices = parsedOfficesCap;
    }
    const parsedDepartmentsCap = parseCap(departmentsCap);
    if (parsedDepartmentsCap !== null) {
      entityCaps.departments = parsedDepartmentsCap;
    }

    await onSave({
      hard_limit_enabled: hardLimitEnabled,
      entity_caps: entityCaps,
      default_office_id: defaultOfficeId.trim(),
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40 text-theme-text-secondary dark:text-dark-theme-text-secondary">
        Loading Dialpad safeguards...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold text-theme-text dark:text-dark-theme-text mb-3">
          Dialpad Safeguards
        </h3>
        <p className="text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary">
          Configure hard limits once per organization for Dialpad as a target platform. When enabled, the backend checks live Dialpad counts before each counted create.
        </p>
      </div>

      <div className="rounded-lg border border-amber-300/40 bg-amber-500/10 p-4">
        <div className="flex items-start space-x-3">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-amber-500" />
          <div className="space-y-1 text-sm text-amber-900 dark:text-amber-200">
            <p>Dialpad departments cover both call queues and auto attendants.</p>
            <p>Teams to Dialpad requires a default office ID because Teams does not create Dialpad offices in v1.</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <label className="flex items-start space-x-3 rounded-lg border border-theme-border dark:border-dark-theme-border p-4">
          <input
            type="checkbox"
            checked={hardLimitEnabled}
            onChange={(event) => setHardLimitEnabled(event.target.checked)}
            className="mt-1 h-4 w-4 rounded border-theme-border text-theme-accent focus:ring-theme-accent"
          />
          <div>
            <p className="font-medium text-theme-text dark:text-dark-theme-text">
              Enable hard-limit enforcement
            </p>
            <p className="text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary">
              Counted Dialpad creates will perform GET-before-POST capacity checks and fail closed when a cap is missing or reached.
            </p>
          </div>
        </label>

        <div className="grid gap-4 md:grid-cols-3">
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-theme-text dark:text-dark-theme-text">
              Users Cap
            </span>
            <input
              aria-label="Users Cap"
              type="number"
              min="0"
              value={usersCap}
              onChange={(event) => setUsersCap(event.target.value)}
              className="w-full rounded-lg border border-theme-border dark:border-dark-theme-border bg-white px-3 py-2 text-theme-text dark:bg-gray-800 dark:text-dark-theme-text"
              placeholder="e.g. 250"
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-medium text-theme-text dark:text-dark-theme-text">
              Offices Cap
            </span>
            <input
              aria-label="Offices Cap"
              type="number"
              min="0"
              value={officesCap}
              onChange={(event) => setOfficesCap(event.target.value)}
              className="w-full rounded-lg border border-theme-border dark:border-dark-theme-border bg-white px-3 py-2 text-theme-text dark:bg-gray-800 dark:text-dark-theme-text"
              placeholder="e.g. 5"
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-medium text-theme-text dark:text-dark-theme-text">
              Departments Cap
            </span>
            <input
              aria-label="Departments Cap"
              type="number"
              min="0"
              value={departmentsCap}
              onChange={(event) => setDepartmentsCap(event.target.value)}
              className="w-full rounded-lg border border-theme-border dark:border-dark-theme-border bg-white px-3 py-2 text-theme-text dark:bg-gray-800 dark:text-dark-theme-text"
              placeholder="Queues + auto attendants"
            />
          </label>
        </div>

        <label className="block">
          <span className="mb-2 block text-sm font-medium text-theme-text dark:text-dark-theme-text">
            Default Office ID
          </span>
          <input
            aria-label="Default Office ID"
            type="text"
            value={defaultOfficeId}
            onChange={(event) => setDefaultOfficeId(event.target.value)}
            className="w-full rounded-lg border border-theme-border dark:border-dark-theme-border bg-white px-3 py-2 text-theme-text dark:bg-gray-800 dark:text-dark-theme-text"
            placeholder="off_123"
          />
          <p className="mt-2 text-xs text-theme-text-secondary dark:text-dark-theme-text-secondary">
            Required for source platforms that do not create Dialpad offices.
          </p>
        </label>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSaving}
            className="rounded-lg bg-theme-accent px-4 py-2 font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 dark:bg-dark-theme-accent"
          >
            {isSaving ? 'Saving...' : 'Save Dialpad Safeguards'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DialpadSafeguardsPanel;
